import React from 'react'
import { useForm } from 'react-hook-form'
import {
  ConfirmTransition,
  ReviewPayload,
  TransferSequence,
  TransferStepConfiguration,
  TransferStepProps,
  TransferSteps
} from '../types'

import { useTransferContext } from '../TransferProvider'
import { TransferForm } from '../forms/ReviewTransferForm'
import { NonTransferableItemsToast } from '../components/NonTransferableItemsToast'
import BulkAssetsActionView from '@/components/BulkAssetsActionView'

export default function ReviewStep({
  sequence,
  ...modalProps
}: TransferStepProps) {
  const context = useTransferContext()
  const {
    transitionTo,
    isFetching,
    isLoading,
    transfer,
    transfer: { assets },
    preview: {
      transferable_assets: { items: transferableAssets }
    }
  } = context

  const methods = useForm<ReviewPayload>({
    defaultValues: {
      to: transfer.to,
      memo: transfer.memo
    },
    mode: 'onChange'
  })

  const {
    formState: { isValid, isSubmitting, isValidating }
  } = methods

  const submitForm = (data: ReviewPayload) => {
    const { memo } = data
    transitionTo<ConfirmTransition>({
      step: 'confirm',
      sequence,
      payload: { memo }
    })
  }

  const shouldDisableSubmit =
    !isValid || isSubmitting || isValidating || !assets?.length

  return (
    <BulkAssetsActionView
      modalProps={modalProps}
      formMethods={methods}
      onClose={sequence.close}
      title="Item Transfer"
      onSubmit={submitForm}
      primaryAction={{
        label: 'Continue',
        disabled: shouldDisableSubmit
      }}
      listConfig={{
        titleSuffix: 'to transfer',
        items: transferableAssets,
        isLoading: isFetching || isLoading,
        preListSlot: <NonTransferableItemsToast className="-mt-1" />,
        onListModalTriggerClick: () =>
          sequence.goTo(TransferSteps.transferableAssets)
      }}
      footerSlot={<NonTransferableItemsToast className="md:hidden mt-3" />}
    >
      <TransferForm />
    </BulkAssetsActionView>
  )
}

ReviewStep.configure = (
  sequence: TransferSequence,
  isMobile: boolean
): TransferStepConfiguration => {
  return {
    id: TransferSteps.review,
    title: isMobile ? 'Item Transfer' : null,
    className: 'md:w-[980px] md:max-w-full !pb-0',
    shouldHideHeader: !isMobile,
    view: props => <ReviewStep {...props} sequence={sequence} />
  }
}
